import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import { jwtDecode } from "jwt-decode";
import { AuthResponse, AuthError, IDTokenPayload } from "../constants/types";
import { getOrgConfig } from "../utils/orgUtils";
import { clearOrganizationId, getOrganizationId } from "../utils/orgIdUtils";
import { normalizeRoles } from "../utils/roleUtils";

console.log("going inside Authservice page");
export class AuthApiService {
  private static userPool: CognitoUserPool;
  static initializeUserPool(orgId: string) {
    const orgConfig = getOrgConfig(orgId);
    if (!orgConfig || !orgConfig.userPoolId || !orgConfig.clientId) {
      throw new Error("Missing Cognito configuration for the organization");
    }

    this.userPool = new CognitoUserPool({
      UserPoolId: orgConfig.userPoolId,
      ClientId: orgConfig.clientId,
    });
  }

  static getUserPool() {
    if (!this.userPool) {
      const orgId = getOrganizationId();
      this.initializeUserPool(orgId ?? "");
    }
  }

  static async login(
    email: string,
    password: string,
    newPassword = ""
  ): Promise<AuthResponse> {
    this.getUserPool();

    try {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const userData = {
        Username: email,
        Pool: this.userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      const result = (await new Promise<AuthResponse>((resolve, reject) =>
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: (result) => {
            console.log("result", result);
            resolve({
              accessToken: result.getAccessToken().getJwtToken(),
              idToken: result.getIdToken().getJwtToken(),
              refreshToken: result.getRefreshToken().getToken(),
            });
          },
          onFailure: (err) => {
            reject(this.handleError(err));
          },
          newPasswordRequired: () => {
            cognitoUser.completeNewPasswordChallenge(
              newPassword,
              {},
              {
                onSuccess: (result) => {
                  console.log("reset new password", result);
                  this.logout();
                  resolve({
                    accessToken: result.getAccessToken().getJwtToken(),
                    idToken: result.getIdToken().getJwtToken(),
                    refreshToken: result.getRefreshToken().getToken(),
                  });
                },
                onFailure: (err) => {
                  reject(this.handleError(err));
                },
              }
            );
          },
        })
      )) as AuthResponse;

      return result;
    } catch (error) {
      throw error as AuthError;
    }
  }

  static async forgotPassword(username: string): Promise<string> {
    this.getUserPool();
    const user = new CognitoUser({
      Username: username,
      Pool: this.userPool,
    });

    return new Promise((resolve, reject) => {
      user.forgotPassword({
        onSuccess: (data) => {
          console.log("data", data);
          resolve("Password reset initiated successfully.");
        },
        onFailure: (err) => {
          reject(this.handleError(err));
        },
      });
    });
  }

  static async confirmPassword(
    username: string,
    confirmationCode: string,
    newPassword: string
  ): Promise<string> {
    this.getUserPool();
    const user = new CognitoUser({
      Username: username,
      Pool: this.userPool,
    });

    return new Promise((resolve, reject) => {
      user.confirmPassword(confirmationCode, newPassword, {
        onSuccess: () => {
          resolve("Password reset confirmed successfully.");
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }

  static async getUserGroups(idToken: string) {
    if (!idToken) {
      throw new Error("User is not logged in or token not available");
    }
    const decodedToken: IDTokenPayload = jwtDecode(idToken);

    const userGroups = decodedToken["cognito:groups"];
    console.log("User Groups:", userGroups);

    return normalizeRoles(userGroups);
  }
  static async getUserEmail(idToken: any) {
    if (!idToken) {
      throw new Error("User is not logged in or token not available");
    }
    const decodedToken: IDTokenPayload = jwtDecode(idToken);

    const userEmail = decodedToken?.email;
    const userName = decodedToken["cognito:username"];

    return { userEmail: userEmail || "", userName: userName || "" };
  }

  static async logout(): Promise<void> {
    this.getUserPool();
    const cognitoUser = this.userPool.getCurrentUser();

    if (!cognitoUser) {
      console.log("No user is currently signed in.");
      return;
    }
    const rememberedUsername = localStorage.getItem("rememberedUsername");
    cognitoUser.signOut();
    console.log("User has been logged out.");
    localStorage.clear();
    if (rememberedUsername) {
      localStorage.setItem("rememberedUsername", rememberedUsername);
    }
  }

  static fetchIdToken(): string | null {
    return localStorage.getItem("idToken");
  }

  static fetchAccessToken(): string | null {
    return localStorage.getItem("accessToken");
  }

  static handleError(err: any): AuthError {
    console.log(err.message);
    switch (err.code) {
      case "UserNotFoundException":
        return { message: "User does not exist", code: 404, errCode: err.code };
      case "NotAuthorizedException":
        return {
          message: "Incorrect username or password",
          code: 401,
          errCode: err.code,
        };
      case "InvalidParameterException":
        return {
          message: "Invalid input parameters",
          code: 400,
          errCode: err.code,
        };
      case "LimitExceededException":
        return {
          message: "Too many requests. Try again later",
          code: 429,
          errCode: err.code,
        };
      default:
        return {
          message: err.message || "An unexpected error occurred",
          code: 500,
          errCode: err.code || "",
        };
    }
  }
}
